import * as Sentry from '@sentry/react';
import { PDFDocument, EncryptedPDFError } from '@cantoo/pdf-lib';
import { MAX_REQUEST_UPLOAD_BYTES } from '../../../types/constants';

/**
 * Pure helpers behind the requested-documents upload (THT-707).
 *
 * They exist so the page can decide *before* it merges or POSTs whether every
 * selected file can actually be combined, and whether what comes out still fits
 * the payload cap — instead of failing opaquely at the merge or at the gateway.
 */

export type PdfStatus =
  | 'ok'
  | 'needs_password'
  | 'wrong_password'
  | 'unreadable';

export interface PdfClassification {
  status: PdfStatus;
  /** Only set when `status === 'ok'` — the decrypted document, ready to copy pages from. */
  doc?: PDFDocument;
}

/**
 * The fork signals password problems with plain `Error`s carrying these exact
 * messages (`EncryptedPDFError` is what you get when no `password` option is
 * passed at all). Matching them lives in this one place: the unit tests assert
 * each mapping against a real fixture, so a fork upgrade that changes the
 * wording fails loudly here rather than silently degrading to "unreadable".
 */
const NEEDS_PASSWORD_MESSAGE = 'NEEDS PASSWORD';
const WRONG_PASSWORD_MESSAGE = 'Password incorrect';

const isPasswordError = (error: unknown): boolean => {
  if (error instanceof EncryptedPDFError) return true;
  if (!(error instanceof Error)) return false;
  return (
    error.message === NEEDS_PASSWORD_MESSAGE ||
    error.message === WRONG_PASSWORD_MESSAGE
  );
};

/**
 * Decides whether a PDF can be merged, and gets back the decrypted document
 * when it can.
 *
 * The empty password is always tried first: owner-password-only PDFs (the
 * standard export of bank statements, insurance certs and e-signed docs) derive
 * their key from an empty *user* password, which is why the customer opens them
 * without a prompt. Trying `''` is what makes those merge with no customer
 * action; a supplied password is only used when the empty one is refused.
 */
export const classifyPdf = async (
  bytes: ArrayBuffer | Uint8Array,
  password?: string
): Promise<PdfClassification> => {
  try {
    const doc = await PDFDocument.load(bytes, { password: '' });
    return { status: 'ok', doc };
  } catch (emptyPasswordError) {
    if (!isPasswordError(emptyPasswordError)) return { status: 'unreadable' };
    if (!password) return { status: 'needs_password' };

    try {
      const doc = await PDFDocument.load(bytes, { password });
      return { status: 'ok', doc };
    } catch (suppliedPasswordError) {
      return {
        status: isPasswordError(suppliedPasswordError)
          ? 'wrong_password'
          : 'unreadable',
      };
    }
  }
};

const extensionOf = (file: File): string =>
  file.name?.split('.').pop()?.toLowerCase() ?? '';

const KNOWN_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
]);

/**
 * The browser does not always give us a usable `file.type`: it is empty for PDFs
 * picked on some Windows/Edge setups, and `application/octet-stream` for images
 * on some Android/Windows ones — both used to drop the file silently out of the
 * merge. The dropzone accepts those files on their extension, so we classify
 * them on their extension too, and only trust `file.type` when it is one we
 * recognise. `handleUpload` already refuses anything that is not
 * pdf/jpg/jpeg/png.
 */
const hasKnownType = (file: File): boolean => KNOWN_TYPES.has(file.type);

export const isPdfFile = (file: File): boolean =>
  file.type === 'application/pdf' ||
  (!hasKnownType(file) && extensionOf(file) === 'pdf');

export const isImageFile = (file: File): boolean => imageKind(file) !== null;

export const imageKind = (file: File): 'jpeg' | 'png' | null => {
  if (file.type === 'image/jpeg' || file.type === 'image/jpg') return 'jpeg';
  if (file.type === 'image/png') return 'png';
  if (hasKnownType(file)) return null;

  const extension = extensionOf(file);
  if (extension === 'jpg' || extension === 'jpeg') return 'jpeg';
  if (extension === 'png') return 'png';
  return null;
};

/** Decoded byte size of a base64 payload, without allocating the bytes. */
export const base64ByteLength = (base64: string): number => {
  const clean = base64.replace(/\s/g, '');
  if (!clean.length) return 0;
  const padding = clean.endsWith('==') ? 2 : clean.endsWith('=') ? 1 : 0;
  return (clean.length / 4) * 3 - padding;
};

export const exceedsUploadCap = (bytes: number): boolean =>
  bytes > MAX_REQUEST_UPLOAD_BYTES;

export type UploadBlockedReason =
  | 'needs_password'
  | 'wrong_password'
  | 'unreadable_pdf'
  | 'combined_too_large';

/**
 * One report per blocked upload, so a failure the customer sees is also a
 * failure we can count. Sentry indexes tags, so only the low-cardinality
 * `reason` and `document_type` go there; sizes and counts go in `extra`.
 *
 * Never pass a file name or a password: this is the only Sentry call on the
 * upload path and its payload is fixed here on purpose.
 */
export const reportUploadBlocked = ({
  reason,
  documentType,
  fileCount,
  inputBytes,
  outputBytes,
}: {
  reason: UploadBlockedReason;
  documentType: string;
  fileCount: number;
  inputBytes: number;
  outputBytes?: number;
}): void => {
  Sentry.captureMessage('requested-documents upload blocked', {
    level: 'warning',
    tags: { reason, document_type: documentType },
    extra: {
      file_count: fileCount,
      input_bytes: inputBytes,
      ...(outputBytes === undefined ? {} : { output_bytes: outputBytes }),
    },
  });
};

/**
 * An unexpected failure on the upload path — not one of the four blocked cases
 * above. It goes to Sentry as an exception so the stack survives, and so the
 * `reason` tag keeps counting real, explainable blocks instead of being a
 * dumping ground for crashes.
 */
export const reportUploadCrashed = (
  error: unknown,
  {
    documentType,
    fileCount,
    inputBytes,
  }: { documentType: string; fileCount: number; inputBytes: number }
): void => {
  Sentry.captureException(error, {
    level: 'error',
    tags: { document_type: documentType },
    extra: { file_count: fileCount, input_bytes: inputBytes },
  });
};

/**
 * Blocked merge, carrying the classification and the file that caused it so the
 * page can name the file in its message.
 */
export class DocumentMergeError extends Error {
  constructor(
    readonly status: Exclude<PdfStatus, 'ok'>,
    readonly fileName: string
  ) {
    super(`Cannot merge ${fileName}: ${status}`);
    this.name = 'DocumentMergeError';
  }
}

/** jsdom and Safari < 14 don't implement `Blob.arrayBuffer`. */
export const readFileBytes = async (file: File): Promise<Uint8Array> => {
  if (typeof file.arrayBuffer === 'function') {
    return new Uint8Array(await file.arrayBuffer());
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () =>
      reject(reader.error ?? new Error(`Failed to read ${file.name}`));
    reader.readAsArrayBuffer(file);
  });
};

const uint8ToBase64 = (bytes: Uint8Array): string => {
  // Chunked so a multi-MB merge doesn't blow the call stack via `apply`.
  let binary = '';
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.slice(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return btoa(binary);
};

const PAGE_WIDTH = 612; // Letter, in points
const PAGE_HEIGHT = 792;
const PAGE_MARGIN = 50;

/**
 * Combines multiple files (PDFs and images) into a single PDF.
 *
 * @param files - Array of files to combine
 * @param getPassword - Password for a file that needs one; kept by the caller in
 *   component state for the duration of the upload only.
 * @returns Promise resolving to the combined PDF as a base64 string
 * @throws DocumentMergeError naming the file that could not be merged
 */
export const combineFilesToPDF = async (
  files: File[],
  getPassword?: (file: File) => string | undefined
): Promise<string> => {
  const pdfDoc = await PDFDocument.create();

  for (const file of files) {
    const fileBytes = await readFileBytes(file);

    if (isPdfFile(file)) {
      const { status, doc } = await classifyPdf(fileBytes, getPassword?.(file));
      if (status !== 'ok' || !doc) {
        throw new DocumentMergeError(
          status === 'ok' ? 'unreadable' : status,
          file.name
        );
      }

      const pages = await pdfDoc.copyPages(doc, doc.getPageIndices());
      pages.forEach((page) => pdfDoc.addPage(page));
      continue;
    }

    const kind = imageKind(file);
    if (!kind) {
      throw new DocumentMergeError('unreadable', file.name);
    }

    const image =
      kind === 'jpeg'
        ? await pdfDoc.embedJpg(fileBytes)
        : await pdfDoc.embedPng(fileBytes);
    const { width, height } = image.size();

    const availableWidth = PAGE_WIDTH - 2 * PAGE_MARGIN;
    const availableHeight = PAGE_HEIGHT - 2 * PAGE_MARGIN;
    const scale = Math.min(availableWidth / width, availableHeight / height, 1);
    const scaledWidth = width * scale;
    const scaledHeight = height * scale;

    const page = pdfDoc.addPage();
    page.setSize(PAGE_WIDTH, PAGE_HEIGHT);
    page.drawImage(image, {
      x: (PAGE_WIDTH - scaledWidth) / 2,
      y: (PAGE_HEIGHT - scaledHeight) / 2,
      width: scaledWidth,
      height: scaledHeight,
    });
  }

  return uint8ToBase64(await pdfDoc.save());
};
